import { useCallback, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ErrorBanner, formatUtc, useAsync } from '../../components/Async'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { Dialog } from '../../components/Dialog'
import { WaysInList } from './WaysInList'
import { WayInInspector } from './WayInInspector'
import { SeatLimitCard } from './SeatLimitCard'
import { useShortcuts } from '../../components/Shortcuts'
import {
  ApiError,
  api,
  deployment,
  enrollmentTokens,
  seatLimit,
  type EnrollmentTokenResponse,
  type WayIn,
  type WayInPage
} from '../../lib/api'
import { paramFrom, withParams } from '../../lib/tabs'

/**
 * Every way into this organization, and the two things that bound them.
 *
 * The tab was four cards in two named columns (D-436). Two of those cards were unbounded lists - kits
 * and tokens - and a column that scrolls forever beside a column two lines tall wastes the height the
 * list needs. So the arrangement is superseded: the agent build and the seat limit become a fixed
 * strip that does not grow, and everything under it is one list (D-441).
 *
 * What D-436 was protecting still holds. The seat ceiling stays visible while a kit is composed, now
 * because it is pinned above the list rather than beside it, and no order is imposed on the work.
 *
 * **The unhealthy states stay on the page, unfolded.** A registered build that is not an installer
 * disables Build a kit and says why, and a seat count at or over the limit takes the danger tint.
 * Neither is behind a disclosure and neither is in the inspector: they are the reason not to press the
 * button beside them (D-432).
 */
export function EnrollmentPage() {
  const location = useLocation()
  const navigate = useNavigate()

  // Read once and handed down, as it was before: the strip needs it and so does the build flow, and
  // two reads could disagree on screen about whether a kit can be built at all.
  const builds = useAsync(() => deployment.builds(), [])
  const seats = useAsync(() => seatLimit.get(), [])

  const [page, setPage] = useState<WayInPage | null>(null)
  const [reloadKey, setReloadKey] = useState(0)
  const [failure, setFailure] = useState<string | null>(null)
  // A refusal from one of the naming dialogs, shown inside it. The page banner sits behind the modal,
  // so a kit the server refused used to look like a button that did nothing: no download, no message.
  const [dialogFailure, setDialogFailure] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const [confirming, setConfirming] = useState<WayIn | null>(null)
  const [issuing, setIssuing] = useState(false)
  const [building, setBuilding] = useState<string | null>(null)
  const [issued, setIssued] = useState<EnrollmentTokenResponse | null>(null)
  const [copied, setCopied] = useState(false)
  const [name, setName] = useState('')

  const selectedId = paramFrom(location.search, 'selected')
  const selected = useMemo(
    () => page?.items.find((row) => row.enrollmentTokenId === selectedId) ?? null,
    [page, selectedId]
  )

  const onLoaded = useCallback((loaded: WayInPage | null) => setPage(loaded), [])

  /*
    The two bindings this screen can honor, registered for as long as it is on view.

    `I` toggles the pane rather than selecting anything: with a row open it closes, and with none open
    it opens the first row, so the key never does nothing. Escape closes. Memoized because the registry
    re-registers on identity change, and a fresh object each render would re-register on every
    keystroke typed into the search box (D-447).
  */
  const shortcuts = useMemo(
    () => ({
      'toggle-inspector': () => {
        if (selectedId) return select(null)
        const first = page?.items[0]
        if (first) select(first)
      },
      dismiss: () => {
        if (selectedId) select(null)
      }
    }),
    // `select` reads `location.search` on each call, so it is deliberately not a dependency: including
    // it would rebuild this object on every navigation, which is every filter change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedId, page]
  )

  useShortcuts(shortcuts)

  /**
   * Opens a row, or closes the pane.
   *
   * In the URL beside the filter, so a link to a kit is a link to that kit. `replace` rather than a
   * push while stepping between rows, so the back button leaves the screen rather than walking back
   * through everything that was clicked on it (D-441).
   */
  function select(row: WayIn | null) {
    navigate(`/devices${withParams(location.search, { selected: row?.enrollmentTokenId ?? null })}`, {
      replace: true
    })
  }

  async function revoke(row: WayIn) {
    setFailure(null)
    setBusy(true)
    try {
      await enrollmentTokens.revoke(row.enrollmentTokenId)
      setReloadKey((key) => key + 1)

      /*
        Back to the list, because the act is finished and the pane's reason for being open went with it.

        Found on a phone, where it is not a nicety. Below 1120px the pane is a sheet that replaces the
        list, so leaving it open after a revoke strands somebody on a panel whose Revoke button has just
        disappeared, with no way to see that anything happened. On a desk it is the same answer for a
        milder reason: the row changing state and leaving the Live filter is the feedback, and that is
        on the list (D-449).
      */
      select(null)
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : 'The token could not be revoked.')
    } finally {
      setBusy(false)
    }
  }

  async function buildKit(kitName: string) {
    setDialogFailure(null)
    setNotice(null)
    setBusy(true)
    try {
      await deployment.buildKit(kitName)
      setBuilding(null)
      setName('')
      setReloadKey((key) => key + 1)
      setNotice('Kit built and downloaded. It contains a live enrollment token — treat it like a credential.')
    } catch (error) {
      setDialogFailure(error instanceof ApiError ? error.message : 'The kit could not be built.')
    } finally {
      setBusy(false)
    }
  }

  async function issueToken(label: string) {
    setDialogFailure(null)
    setBusy(true)
    try {
      setIssued(await api.issueEnrollmentToken(label))
      setIssuing(false)
      setName('')
      setReloadKey((key) => key + 1)
    } catch (error) {
      setDialogFailure(error instanceof ApiError ? error.message : 'The enrollment token could not be issued.')
    } finally {
      setBusy(false)
    }
  }

  async function copyToken(token: string) {
    try {
      await navigator.clipboard.writeText(token)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 4000)
    } catch {
      // Clipboard access needs a secure context. Selecting the text leaves the operator holding
      // something rather than a button that appeared to do nothing.
      const node = document.querySelector('[data-testid="enrollment-token"]')
      if (node) window.getSelection()?.selectAllChildren(node)
      setFailure('This browser would not let the page write to the clipboard. The token is selected; copy it with Ctrl+C.')
    }
  }

  const rows = builds.value ?? []
  const current = rows.find((build) => build.channel === 'stable') ?? rows[0]
  const notAnInstaller = Boolean(current && !current.fileName.toLowerCase().endsWith('.msi'))
  /*
    Only the absence of a build stops the button.

    `02-screen.md` asks for it to be disabled when the registered build is not an installer as well,
    and that is not taken. The extension check is a heuristic, not a validation - the server accepts
    the build and is the authority on it - and hard-blocking on a filename strands any customer whose
    CI publishes something this console did not anticipate. That matters here because the product is
    sold to managed service providers, who repackage. It would also break the arrangement D-117 chose
    deliberately: the end-to-end suite registers the distribution share's own README as a stand-in on
    every run, precisely so that no file named like an installer but not one is ever checked in.

    So the warning is loud, unfolded, and on the reason rather than on the control - which is what
    D-416 actually asked for - and the operator is still allowed to proceed (D-445).
  */
  const cannotBuild = rows.length === 0
  const whyNot = rows.length === 0
    ? 'No agent build is registered, so there is nothing to wrap a kit around.'
    : notAnInstaller
      ? `The registered build is ${current!.fileName}, which is not an installer.`
      : undefined

  const seat = seats.value
  const over = seat ? seat.inUse >= seat.seatLimit : false
  const counts = page?.counts
  const meta = counts
    ? `${(counts.all ?? 0).toLocaleString()} kits and tokens · ${(counts.live ?? 0).toLocaleString()} live`
    : ''

  return (
    <div className="ways-in" data-inspector={selected ? 'open' : 'closed'}>
      <div className="page-header">
        <div>
          <p className="lede" data-testid="ways-in-meta">
            {meta || 'Every way into this organization: each kit, and the token inside it.'}
          </p>
        </div>
        <div className="toolbar">
          <button type="button" onClick={() => setIssuing(true)} data-testid="issue-enrollment-token">
            Issue a token
          </button>
          <button
            type="button"
            className="primary"
            onClick={() => setBuilding('')}
            disabled={cannotBuild}
            title={whyNot}
            data-testid="build-kit"
          >
            Build a kit
          </button>
        </div>
      </div>

      <ErrorBanner message={failure ?? builds.error} />
      {notice && (
        <div className="banner success" role="status" data-testid="deployment-notice">
          {notice}
        </div>
      )}

      {/*
        The one warning that must not fold. The end-to-end suite registers the distribution share's own
        README as a stand-in on every run, so a kit built in that window carries five files and no
        agent - which somebody downloaded, unzipped, and went looking for the MSI in (D-416). The build
        button is disabled above with the same sentence on its title.
      */}
      {notAnInstaller && (
        <div className="banner warning" data-testid="build-not-an-installer">
          The current build is <code>{current!.fileName}</code>, which is not an installer. A kit built
          now would carry that file instead of the agent, and installing it would do nothing. Register
          the real installer again before handing a kit to anybody.
        </div>
      )}

      <div className="fixed-strip">
        <div className="strip-group">
          <span className="strip-eyebrow">Agent build</span>
          {current ? (
            <span className="strip-value" data-testid="current-build">
              <span className="mono">{current.fileName}</span>
              <span className="chip chip-ack">{current.channel}</span>
            </span>
          ) : (
            <span className="strip-value muted" data-testid="no-build">
              None registered
            </span>
          )}
        </div>

        <span className="strip-divider" aria-hidden="true" />

        <div className="strip-group">
          <span className="strip-eyebrow">Device seats</span>
          <span className="strip-value">
            {seat ? (
              <>
                <span className={over ? 'tabular danger' : 'tabular'} data-testid="strip-seat-usage">
                  {seat.inUse.toLocaleString()} of {seat.seatLimit.toLocaleString()} —{' '}
                  {seat.remaining.toLocaleString()} left
                </span>
                <span className="meter strip-meter">
                  <span
                    className={over ? 'meter-fill over' : 'meter-fill'}
                    style={{ width: `${Math.min(100, Math.round((seat.inUse / Math.max(seat.seatLimit, 1)) * 100))}%` }}
                  />
                </span>
              </>
            ) : (
              <span className="muted">—</span>
            )}
            <a href="#seats" data-testid="change-seats">
              Change
            </a>
          </span>
        </div>

        {/* Written for the reader of the code as much as for the operator: these two do not grow, and
            that is why they are allowed to sit above a list that does. */}
        <span className="strip-note">Neither of these grows. They sit here so the list below gets the height.</span>
      </div>

      <div className="ways-in-body">
        <WaysInList selected={selectedId} onSelect={select} onLoaded={onLoaded} reloadKey={reloadKey} />

        {selected && (
          <WayInInspector
            row={selected}
            onClose={() => select(null)}
            onRevoke={setConfirming}
            onBuildAnother={(row) => setBuilding(row.name)}
          />
        )}
      </div>

      {/* The seat editor, kept on the tab but out of the list's way. The strip links down to it. */}
      <div id="seats" className="seats-anchor">
        <SeatLimitCard />
      </div>

      <ConfirmDialog
        open={confirming !== null}
        title={confirming ? `Revoke ${confirming.name}` : 'Revoke it'}
        confirmLabel="Revoke it for good"
        danger
        busy={busy}
        // Closed before the request, so a refusal lands on the list rather than under a modal.
        onConfirm={() => {
          if (!confirming) return
          const row = confirming
          setConfirming(null)
          void revoke(row)
        }}
        onClose={() => setConfirming(null)}
        data-testid="revoke-token-confirm"
      >
        <p>
          No further machine can join with this token, and the kit built around it stops working —
          including one somebody is part-way through rolling out.
        </p>
        <p>
          The {confirming?.uses.toLocaleString() ?? 0} machines that already joined with it keep working
          and keep reporting. Nothing is sent to them.
        </p>
        <p className="hint">
          This cannot be undone. The token was shown once when it was issued and is not stored here, so
          there is nothing to switch back on.
        </p>
      </ConfirmDialog>

      <NameDialog
        open={building !== null}
        title="Build a kit"
        what="kit"
        value={name || building || ''}
        busy={busy}
        error={dialogFailure}
        onChange={(value) => {
          setName(value)
          setDialogFailure(null)
        }}
        onClose={() => {
          setBuilding(null)
          setDialogFailure(null)
        }}
        onSubmit={(value) => void buildKit(value)}
        data-testid="build-kit-dialog"
      >
        <p className="muted" style={{ marginTop: 0 }}>
          The zip holds the installer, a script that points it at this server, and the policies this
          tenant has published. It downloads once: it carries a live enrollment token, so asking again
          issues a new one rather than handing back the old file.
        </p>
        <p className="hint">
          It allows as many machines as this organization has device seats. That is not a number per
          kit — two kits each carrying their own count would silently double what could be enrolled.
        </p>
      </NameDialog>

      <NameDialog
        open={issuing}
        title="Issue a token"
        what="token"
        value={name}
        busy={busy}
        error={dialogFailure}
        onChange={(value) => {
          setName(value)
          setDialogFailure(null)
        }}
        onClose={() => {
          setIssuing(false)
          setDialogFailure(null)
        }}
        onSubmit={(value) => void issueToken(value)}
        data-testid="issue-token-dialog"
      >
        <p className="muted" style={{ marginTop: 0 }}>
          A token on its own, for a machine that already has the agent installed — a re-image, or a
          rollout driven by something other than the script. It is the same credential without the
          installer around it.
        </p>
        <p className="hint">It is shown once, here, and is not stored.</p>
      </NameDialog>

      <Dialog
        open={issued !== null}
        title="Copy this token now"
        onClose={() => setIssued(null)}
        data-testid="issued-token-dialog"
      >
        {issued && (
          <>
            <div className="banner warning" role="note">
              This token is shown once and is not stored. Copy it now; if it is lost, issue another.
            </div>

            <div className="toolbar">
              <div className="token-reveal grow" data-testid="enrollment-token">
                {issued.token}
              </div>
              <button type="button" onClick={() => copyToken(issued.token)} data-testid="copy-enrollment-token">
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>

            <p className="hint" data-testid="enrollment-token-expiry">
              Expires {formatUtc(issued.expiresUtc)}, which is this organization's credential lifetime.
              Devices redeem it at <code>{issued.enrollEndpoint}</code>.
            </p>

            <div className="dialog-actions">
              <button type="button" className="primary" onClick={() => setIssued(null)} data-testid="issued-token-done">
                Done
              </button>
            </div>
          </>
        )}
      </Dialog>
    </div>
  )
}

/**
 * Asks for a name before making a way in, and will not proceed without one.
 *
 * Required, which it was not. The console labelled a bare token with `new Date().toISOString()`, and
 * in a list that is sorted, searched and filtered by name a machine-generated timestamp is the one row
 * nobody can find - the development stack has fourteen hundred of them. Existing rows keep theirs;
 * nothing rewrites them (D-444).
 */
function NameDialog({
  open,
  title,
  what,
  value,
  busy,
  error,
  onChange,
  onClose,
  onSubmit,
  children,
  'data-testid': testId
}: {
  open: boolean
  title: string
  what: string
  value: string
  busy: boolean
  error?: string | null
  onChange: (value: string) => void
  onClose: () => void
  onSubmit: (value: string) => void
  children: React.ReactNode
  'data-testid'?: string
}) {
  const named = value.trim().length > 0

  return (
    <Dialog open={open} title={title} onClose={onClose} data-testid={testId}>
      {children}

      <form
        onSubmit={(event) => {
          event.preventDefault()
          if (named) onSubmit(value.trim())
        }}
      >
        <div className="field">
          <label htmlFor={`${what}-name`}>Name</label>
          <input
            id={`${what}-name`}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            placeholder={what === 'kit' ? 'Head office' : 'Re-image bench'}
            data-testid={`${what}-name`}
          />
          <div className="hint">
            What this is for, in the words somebody would search for later. It is how this {what} is
            found in the list.
          </div>
        </div>

        {error && (
          <div data-testid={`${what}-failure`}>
            <ErrorBanner message={error} />
          </div>
        )}

        <div className="dialog-actions">
          <button type="button" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="primary" disabled={busy || !named} data-testid={`${what}-submit`}>
            {title}
          </button>
        </div>
      </form>
    </Dialog>
  )
}
